import { z } from 'zod';
/**
 * A single entity on the analyst's watchlist, plus the nightly cron's
 * cursor into that entity's graph state.
 *
 * `lastScannedAt` is when `refreshWatchlistSignals` last scanned this item;
 * `factCount`, `sourceCount`, `neighborHash` are the cached delta cues the
 * cron diffs against on the next run to decide whether to emit a Signal.
 */
export const WatchlistItem = z.object({
    entityName: z.string().min(1),
    addedAt: z.string(),
    lastScannedAt: z.string().optional(),
    factCount: z.number().int().nonnegative().optional(),
    sourceCount: z.number().int().nonnegative().optional(),
    neighborHash: z.string().optional(),
    /** Free-text grouping the analyst chooses (e.g. "lobby", "officials").
     *  Empty/undefined = "Uncategorized" in the UI. */
    category: z.string().max(60).optional(),
    /** Sticky-to-top in the watchlist UI; orthogonal to category. */
    pinned: z.boolean().optional(),
    /** ISO datetime — when set in the future, signals for this entity
     *  are suppressed in the active-signals feed until that time. Past
     *  or undefined = unmuted. Lets analysts silence noisy entities
     *  without removing them entirely. */
    mutedUntil: z.string().optional(),
    /** Short analyst note ("focus area: 2024 H2"); shown in the panel
     *  + sortable. Capped to keep the watchlist doc under Firestore's
     *  1MB limit even with hundreds of items. */
    note: z.string().max(280).optional(),
});
/**
 * Patch shape for `PATCH /api/watchlist/items/:name`. Each field is
 * `nullable.optional()` — `undefined` means "leave alone", `null` means
 * "clear it". The store normalizes nulls to field-deletion so a
 * cleared note doesn't linger as an empty string.
 */
export const UpdateWatchlistItemRequest = z.object({
    category: z.string().max(60).nullable().optional(),
    pinned: z.boolean().nullable().optional(),
    mutedUntil: z.string().nullable().optional(),
    note: z.string().max(280).nullable().optional(),
});
/**
 * Shared muting predicate. Lives here (not on the server or the client)
 * so the signals-feed filter on the server and the visual-treatment
 * predicate on the client agree by construction. A future change to
 * mute semantics (e.g. timezone handling) is one edit, not two.
 */
export function isWatchlistItemMuted(item, now = new Date()) {
    if (!item.mutedUntil)
        return false;
    const t = Date.parse(item.mutedUntil);
    return Number.isFinite(t) && t > now.getTime();
}
/**
 * Per-user-per-org watchlist document. Stored at
 * `watchlist/{orgId}__{uid}` in Firestore — the compound id keeps rules
 * simple and writes single-doc while scoping watchlists to the active org.
 *
 * `lastVisitedAt` is stamped when the user opens the command-center; the
 * server uses it to compute the "since you last looked" delta surfaced in
 * the morning-brief header.
 */
export const Watchlist = z.object({
    uid: z.string(),
    orgId: z.string(),
    items: z.array(WatchlistItem).default([]),
    lastVisitedAt: z.string().optional(),
    // Separate from `lastVisitedAt` so the "since you last looked" delta
    // stays accurate even when the server refreshes signals in the
    // background. Stamped by `refreshWatchlistSignalsForOwner`.
    lastRefreshedAt: z.string().optional(),
    updatedAt: z.string(),
});
/**
 * The delta the server computes when the user hits POST /api/watchlist/visited.
 *
 * `sinceIso` is the previous `lastVisitedAt` (null = first visit ever), so
 * the client can render it as "since 14 hours ago" or "since your last
 * session on March 12". `newSignalCount` is the count of signals emitted
 * after that cursor.
 */
export const WatchlistDelta = z.object({
    sinceIso: z.string().nullable(),
    newSignalCount: z.number().int().nonnegative(),
    entitiesCrossedThreshold: z.array(z.string()),
    newSourceDocCount: z.number().int().nonnegative(),
});
